import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '../services/api';

const formatDate = (date) => date.toISOString().slice(0, 10);

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const WorkyardPage = () => {
  const [startDate, setStartDate] = useState(() => formatDate(addDays(new Date(), -6)));
  const [endDate, setEndDate] = useState(() => formatDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [viewMode, setViewMode] = useState('project');
  const [groupByOwner, setGroupByOwner] = useState(true);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(
        `/api/expenses/workyard/summary?start_date=${startDate}&end_date=${endDate}`
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to load Workyard data');
      }
      setSummary(result);
    } catch (err) {
      setError(err.message);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchSummary();
    // Load applies a new range. This only fills the default week.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedData = useMemo(() => {
    if (!summary?.rows) return {};
    const grouped = {};

    summary.rows.forEach((row) => {
      const teamKey = row.team_name || 'Unassigned';
      const ownerKey = groupByOwner ? (row.customer_name || 'Unassigned') : 'All Owners';

      if (!grouped[teamKey]) grouped[teamKey] = {};
      if (!grouped[teamKey][ownerKey]) grouped[teamKey][ownerKey] = {};

      const primaryKey = viewMode === 'project' ? `${row.project_id}` : `${row.employee_id}`;
      if (!grouped[teamKey][ownerKey][primaryKey]) {
        grouped[teamKey][ownerKey][primaryKey] = {
          primaryLabel: viewMode === 'project' ? row.project_name : row.employee_name,
          rows: []
        };
      }
      grouped[teamKey][ownerKey][primaryKey].rows.push(row);
    });

    return grouped;
  }, [summary, viewMode, groupByOwner]);

  const groupTotals = useMemo(() => {
    if (!summary?.rows) return {};
    const totals = {};
    summary.rows.forEach((row) => {
      const teamKey = row.team_name || 'Unassigned';
      const ownerKey = groupByOwner ? (row.customer_name || 'Unassigned') : 'All Owners';
      const primaryKey = viewMode === 'project' ? `${row.project_id}` : `${row.employee_id}`;
      const key = `${teamKey}::${ownerKey}::${primaryKey}`;
      if (!totals[key]) {
        totals[key] = { total_hours: 0, total_cost: 0 };
      }
      totals[key].total_hours += row.total_hours || 0;
      totals[key].total_cost += row.total_cost || 0;
    });
    return totals;
  }, [summary, viewMode, groupByOwner]);

  const formatCurrency = (value) => `$${(value || 0).toFixed(2)}`;

  const buildTotals = (rows) => {
    const totals = {
      total_hours: 0,
      total_cost: 0,
      hours_by_date: {},
      cost_by_date: {}
    };
    dates.forEach((date) => {
      totals.hours_by_date[date] = 0;
      totals.cost_by_date[date] = 0;
    });

    rows.forEach((row) => {
      totals.total_hours += row.total_hours || 0;
      totals.total_cost += row.total_cost || 0;
      dates.forEach((date) => {
        totals.hours_by_date[date] += row.hours_by_date?.[date] || 0;
        totals.cost_by_date[date] += row.cost_by_date?.[date] || 0;
      });
    });

    return totals;
  };

  const teamTotals = useMemo(() => {
    if (!summary?.rows) return {};
    const totals = {};
    summary.rows.forEach((row) => {
      const teamKey = row.team_name || 'Unassigned';
      if (!totals[teamKey]) totals[teamKey] = [];
      totals[teamKey].push(row);
    });
    return totals;
  }, [summary]);

  const ownerTotals = useMemo(() => {
    if (!summary?.rows) return {};
    const totals = {};
    summary.rows.forEach((row) => {
      const teamKey = row.team_name || 'Unassigned';
      const ownerKey = groupByOwner ? (row.customer_name || 'Unassigned') : 'All Owners';
      const key = `${teamKey}::${ownerKey}`;
      if (!totals[key]) totals[key] = [];
      totals[key].push(row);
    });
    return totals;
  }, [summary, groupByOwner]);

  const getDailyHours = (employeeId, date) => {
    return summary?.daily_hours_by_employee?.[employeeId]?.[date] || 0;
  };

  const getHourCellClass = (employeeId, date) => {
    const totalHours = getDailyHours(employeeId, date);
    if (totalHours > 6) return 'bg-green-100';
    if (totalHours >= 4) return 'bg-yellow-100';
    if (totalHours > 0) return 'bg-red-100';
    return '';
  };

  const dates = summary?.dates || [];

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border rounded px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={fetchSummary}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Loading...' : 'Load Workyard'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Daily cost is allocated to each property based on that day’s hours per property.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-gray-600">View:</span>
          <button
            type="button"
            onClick={() => setViewMode('project')}
            className={`px-3 py-1 rounded border ${
              viewMode === 'project'
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-gray-300 text-gray-600'
            }`}
          >
            By Property
          </button>
          <button
            type="button"
            onClick={() => setViewMode('member')}
            className={`px-3 py-1 rounded border ${
              viewMode === 'member'
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-gray-300 text-gray-600'
            }`}
          >
            By Team Member
          </button>
          <div className="flex items-center gap-2 ml-4">
            <input
              id="workyard-group-owner"
              type="checkbox"
              checked={groupByOwner}
              onChange={(e) => setGroupByOwner(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="workyard-group-owner" className="text-gray-700">
              Group by Owner
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded border border-red-200">
          {error}
        </div>
      )}

      {!loading && summary && summary.rows?.length === 0 && (
        <div className="bg-white p-6 rounded-lg shadow text-gray-600">
          No Workyard entries found for this date range.
        </div>
      )}

      {Object.entries(groupedData).map(([teamName, owners]) => (
        <div key={teamName} className="space-y-6">
          {(() => {
            const totals = buildTotals(teamTotals[teamName] || []);
            return (
              <div className="bg-white p-4 rounded-lg shadow flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold">Team: {teamName}</h3>
                <div className="text-sm text-gray-700">
                  <span className="mr-4">Team Hours: {totals.total_hours.toFixed(2)}</span>
                  <span className="font-semibold">Team Cost: {formatCurrency(totals.total_cost)}</span>
                </div>
              </div>
            );
          })()}

          {Object.entries(owners).map(([ownerName, primaryGroups]) => (
            <div key={ownerName} className="space-y-4">
              {groupByOwner && (
                (() => {
                  const totals = buildTotals(ownerTotals[`${teamName}::${ownerName}`] || []);
                  return (
                    <div className="bg-white p-4 rounded-lg shadow ml-4 border-l-4 border-blue-100 flex flex-wrap items-center justify-between gap-3">
                      <h4 className="text-md font-semibold">Owner: {ownerName}</h4>
                      <div className="text-sm text-gray-700">
                        <span className="mr-4">Owner Hours: {totals.total_hours.toFixed(2)}</span>
                        <span className="font-semibold">Owner Cost: {formatCurrency(totals.total_cost)}</span>
                      </div>
                    </div>
                  );
                })()
              )}

              {Object.entries(primaryGroups).map(([primaryId, primaryData]) => {
                const totalsKey = `${teamName}::${ownerName}::${primaryId}`;
                const totals = groupTotals[totalsKey] || { total_hours: 0, total_cost: 0 };
                const headerLabel = viewMode === 'project'
                  ? `Property: ${primaryData.primaryLabel}`
                  : `Team Member: ${primaryData.primaryLabel}`;
                const subtotalHoursByDate = {};
                const subtotalCostByDate = {};
                dates.forEach((date) => {
                  subtotalHoursByDate[date] = 0;
                  subtotalCostByDate[date] = 0;
                });
                primaryData.rows.forEach((row) => {
                  dates.forEach((date) => {
                    subtotalHoursByDate[date] += row.hours_by_date?.[date] || 0;
                    subtotalCostByDate[date] += row.cost_by_date?.[date] || 0;
                  });
                });

                return (
                  <div key={primaryId} className="bg-white rounded-lg shadow ml-8">
                    <div className="px-6 py-4 border-b flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h5 className="text-md font-semibold">{headerLabel}</h5>
                      </div>
                      <div className="text-sm text-gray-700">
                        <span className="mr-4">Subtotal Hours: {totals.total_hours.toFixed(2)}</span>
                        <span className="font-semibold">Subtotal Cost: {formatCurrency(totals.total_cost)}</span>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-4 py-2 border-b">
                              {viewMode === 'project' ? 'Team Member' : 'Property'}
                            </th>
                            <th className="text-left px-4 py-2 border-b">Pay Rate</th>
                            {dates.map((date) => (
                              <th key={date} className="text-center px-3 py-2 border-b">
                                {date}
                              </th>
                            ))}
                            <th className="text-center px-4 py-2 border-b">Total Hours</th>
                            <th className="text-center px-4 py-2 border-b">Total Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {primaryData.rows.map((row) => {
                            const payRateLabel = row.pay_rate != null
                              ? formatCurrency(row.pay_rate)
                              : 'N/A';
                            const rowLabel = viewMode === 'project'
                              ? row.employee_name
                              : row.project_name;
                            const rowId = viewMode === 'project'
                              ? row.employee_id
                              : row.project_id;

                            return (
                              <tr key={`${row.employee_id}-${row.project_id}`}>
                                <td className="px-4 py-2 border-b">{rowLabel}</td>
                                <td className="px-4 py-2 border-b">{payRateLabel}</td>
                                {dates.map((date) => (
                                  <td
                                    key={`${rowId}-${date}`}
                                    className={`text-center px-3 py-2 border-b ${getHourCellClass(row.employee_id, date)}`}
                                  >
                                    {(row.hours_by_date?.[date] || 0).toFixed(2)}
                                  </td>
                                ))}
                                <td className="text-center px-4 py-2 border-b">
                                  {(row.total_hours || 0).toFixed(2)}
                                </td>
                                <td className="text-center px-4 py-2 border-b">
                                  {formatCurrency(row.total_cost)}
                                </td>
                              </tr>
                            );
                          })}
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-4 py-2 border-b">Subtotal</td>
                        <td className="px-4 py-2 border-b" />
                        {dates.map((date) => (
                          <td key={`subtotal-${date}`} className="text-center px-3 py-2 border-b">
                            {subtotalHoursByDate[date].toFixed(2)} / {formatCurrency(subtotalCostByDate[date])}
                          </td>
                        ))}
                        <td className="text-center px-4 py-2 border-b">
                          {totals.total_hours.toFixed(2)}
                        </td>
                        <td className="text-center px-4 py-2 border-b">
                          {formatCurrency(totals.total_cost)}
                        </td>
                      </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default WorkyardPage;
